import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";

export const useContactPhotoUrl = (contactId: number | null, cachedPhotoUrl?: string | null) => {
  const {
    data: photoUrl,
    isLoading,
    error,
    refetch: refreshPhotoUrl,
  } = useQuery({
    queryKey: ["contact-photo-url", contactId],
    queryFn: async () => {
      if (!contactId) return null;

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;

      if (!accessToken) {
        throw new Error("No access token available");
      }

      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/media/profile-picture/url?contact_id=${contactId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch photo URL");
      }

      const responseData = await response.json();

      // Check if the response contains an error message
      if (responseData.error) {
        throw new Error(responseData.error);
      }

      return responseData.photoUrl; // This will be null if no profile picture exists
    },
    enabled: !!contactId,
    placeholderData: cachedPhotoUrl ?? undefined,
    staleTime: 90 * 1000, // 90 seconds - presigned URLs expire at 120s
    gcTime: 10 * 60 * 1000,
  });

  return {
    photoUrl,
    isLoading,
    error: error?.message || null,
    refreshPhotoUrl: () => refreshPhotoUrl(),
  };
};
